/**
 * Pixabay image + vector search adapter. Normalized interface for the unified image picker.
 * Exports two adapter instances: pixabayPhotos and pixabayVectors.
 * Vector download uses MIME + content sniff + extension fallback to detect true SVG vs raster.
 */

import { getStoredValue, setStoredValue } from './api-key-storage.js';

const API_URL = 'https://pixabay.com/api/';
const PER_PAGE = 30;
const SEARCH_TIMEOUT_MS = 10_000;
const DOWNLOAD_TIMEOUT_MS = 15_000;
const LS_KEY = 'pb-pixabay-api-key';

const MIME_TO_EXT = {
  'image/jpeg': '.jpg',
  'image/png': '.png',
  'image/webp': '.webp',
  'image/gif': '.gif',
  'image/svg+xml': '.svg'
};

/**
 * Detect whether a downloaded file is actually SVG using MIME, content sniff, and URL extension.
 */
function detectSvg(blob, url) {
  // Check MIME
  if (blob.type === 'image/svg+xml') return true;

  // Check URL extension
  try {
    const pathname = new URL(url).pathname.toLowerCase();
    if (pathname.endsWith('.svg')) return true;
  } catch {
    /* ignore */
  }

  return false;
}

async function sniffSvgContent(blob) {
  // Read first 256 bytes to check for SVG markers
  const slice = blob.slice(0, 256);
  const text = await slice.text();
  const trimmed = text.trimStart().toLowerCase();
  return trimmed.startsWith('<?xml') || trimmed.startsWith('<svg');
}

function createAdapter(imageType) {
  const isVector = imageType === 'vector';

  return {
    name: isVector ? 'Pixabay Vectors' : 'Pixabay',
    needsKey: true,
    lsKey: LS_KEY,
    attribution: isVector ? 'Vectors provided by Pixabay' : 'Images provided by Pixabay',
    keyHelp: 'Get a free key at pixabay.com/api/docs/',

    getApiKey() {
      return getStoredValue(LS_KEY);
    },

    setApiKey(key) {
      setStoredValue(LS_KEY, key);
    },

    async search(query, page, signal) {
      const key = this.getApiKey();
      if (!key) throw Object.assign(new Error('No API key'), { code: 'NO_KEY' });

      const params = new URLSearchParams({
        key,
        q: query,
        image_type: imageType,
        per_page: String(PER_PAGE),
        page: String(page),
        safesearch: 'true'
      });

      const combined = signal
        ? AbortSignal.any([signal, AbortSignal.timeout(SEARCH_TIMEOUT_MS)])
        : AbortSignal.timeout(SEARCH_TIMEOUT_MS);

      const resp = await fetch(`${API_URL}?${params}`, { signal: combined });

      if (resp.status === 401 || resp.status === 403) {
        throw Object.assign(new Error('Invalid API key'), { code: 'AUTH' });
      }
      if (resp.status === 429) throw Object.assign(new Error('Rate limited'), { code: 'RATE' });
      if (!resp.ok) throw new Error(`Pixabay API error: ${resp.status}`);

      const data = await resp.json();

      return {
        results: data.hits.map((h) => ({
          id: String(h.id),
          thumb: h.webformatURL,
          title: h.tags || 'Image',
          author: h.user,
          authorUrl: `https://pixabay.com/users/${h.user}-${h.user_id}/`,
          _raw: h
        })),
        total: data.totalHits
      };
    },

    async download(item) {
      const hit = item._raw;
      const downloadUrl = hit.largeImageURL;

      const resp = await fetch(downloadUrl, {
        signal: AbortSignal.timeout(DOWNLOAD_TIMEOUT_MS)
      });
      if (!resp.ok) throw new Error(`Download failed: ${resp.status}`);

      const blob = await resp.blob();

      // For vectors: detect if it's actually SVG via MIME + URL extension + content sniff
      if (isVector) {
        let isSvg = detectSvg(blob, downloadUrl);
        if (!isSvg) {
          isSvg = await sniffSvgContent(blob);
        }

        if (isSvg) {
          return new File([blob], `pixabay-${hit.id}.svg`, { type: 'image/svg+xml' });
        }
        // Raster fallback — caller will route to image pipeline, not replaceSvg()
      }

      const mime = blob.type || resp.headers.get('Content-Type') || 'image/jpeg';
      const ext = MIME_TO_EXT[mime] || '.jpg';
      return new File([blob], `pixabay-${hit.id}${ext}`, { type: mime });
    }
  };
}

export const pixabayPhotos = createAdapter('photo');
export const pixabayVectors = createAdapter('vector');
